import { TableRes } from '../../../shared/types';
import { postRequest } from '../../../shared/utils';

/**
 * Checks a list of HTZ/Bauteilindex combinations against the database
 * to find any existing entries.
 *
 * @param rows An array of objects, each containing 'htz' and 'bauteilindex'.
 * @returns A promise that resolves to an array of conflicting row objects
 * (e.g., [{ "htz": "...", "bauteilindex": "..." }, ...])
 * from the database.
 */
export const getConflictingCombinations = async (
  rows: { htz: string; bauteilindex: string }[],
  htzBauteilindexScreenId: number,
  tableDetails: TableRes | undefined,
): Promise<any[]> => {
  const schemaName = tableDetails?.mbdDataGrid?.schemaName || 'npi';

  //  Identify unique combinations to check
  const uniqueCombinations = rows.reduce((acc, current) => {
    const key = `${current.htz}-${current.bauteilindex}`;
    if (!acc.has(key)) {
      acc.set(key, { htz: current.htz, bauteilindex: current.bauteilindex });
    }
    return acc;
  }, new Map());

  const combinationsToCheck: { htz: string; bauteilindex: string }[] = Array.from(
    uniqueCombinations.values(),
  );

  //  Create an array of API promises for concurrent execution
  const checkPromises = combinationsToCheck.map(async (row) => {
    const filterModel = {
      items: [
        { field: 'htz', operator: 'equals', value: row.htz },
        { field: 'bauteilindex', operator: 'equals', value: row.bauteilindex },
      ],
      logicOperator: 'and',
    };

    const body = {
      columns: ['htz', 'bauteilindex'],
      filterModel,
      limitNumber: 1,
      screenGridId: htzBauteilindexScreenId,
      schema: schemaName,
      table: 'sheet1',
      key: 'htz',
      orderCol: 'htz',
      order: 'asc',
    };

    const response = await postRequest('/api/records', body);

    return {
      response: response,
      htz: row.htz,
      bauteilindex: row.bauteilindex,
    };
  });

  const results = await Promise.all(checkPromises);

  const conflictingRows: any[] = [];
  results.forEach((result) => {
    const response = result.response.data;
    const dbRows = response?.[`${schemaName}_sheet1`] || [];
    // If dbRows has content, it means this combination already exists
    if (dbRows.length > 0) {
      conflictingRows.push(dbRows[0]);
    }
  });

  return conflictingRows;
};
